/**
 * uptimeUtils.js — pure helpers for running the Order Board on an always-on
 * kitchen display (24/7 uptime, no one tending it).
 *
 * Three problems these solve:
 *   1. Browsers throttle/suspend timers in a hidden tab or across OS sleep, so on
 *      wake the board can show a stale clock and a stale order list.
 *   2. A 1-second re-render timer that runs forever is wasteful once the "Xs ago"
 *      readout no longer needs per-second precision.
 *   3. An unattended tab left up for weeks never picks up a deploy and can drift.
 *
 * No React, no DOM, no Firestore — fully unit-tested.
 */
import { localDateISO } from './dateUtils';

/**
 * How often the sync bar should re-render its "Xs ago" readout.
 *
 * Per-second precision only matters while the last fetch is recent; after that a
 * coarse tick keeps an always-on screen from burning a timer every second forever
 * (3600 renders/hour → ~66).
 *
 * @param {number|null} secAgo — seconds since the last successful fetch
 * @param {Object} [opts]
 * @returns {number} delay in ms until the next re-render
 */
export function syncTickMs(secAgo, { fineMs = 1000, coarseMs = 10000, fineWindowSec = 60 } = {}) {
  if (secAgo == null || !Number.isFinite(secAgo)) return coarseMs;
  return secAgo < fineWindowSec ? fineMs : coarseMs;
}

/**
 * Should we force a data refetch now that the tab is visible/focused again?
 *
 * Only when the last successful fetch is genuinely old — otherwise simply
 * alt-tabbing back to the board would hammer the Square API on every focus.
 *
 * @param {Object} params
 * @param {number|null} params.lastFetchMs — timestamp of the last good fetch
 * @param {number} params.nowMs
 * @param {number} [params.minGapMs]       — don't refetch if fresher than this
 * @returns {boolean}
 */
export function shouldRefetchOnWake({ lastFetchMs, nowMs, minGapMs = 5000 }) {
  if (!lastFetchMs) return true;                 // never fetched — always sync
  return nowMs - lastFetchMs >= minGapMs;
}

/**
 * Nightly self-restart for an unattended screen: picks up the latest deploy and
 * clears any memory drift from weeks of uptime.
 *
 * Deliberately conservative — it fires ONLY when all of these hold:
 *   • the board is idle (no active tickets), so it can never interrupt service
 *   • the local clock is inside the configured hour
 *   • it hasn't already run today (caller persists `lastReloadDay`)
 *
 * @param {Object} params
 * @param {Date|number} params.now
 * @param {number} [params.reloadHour]        — local hour (0-23) to restart in
 * @param {string|null} [params.lastReloadDay]— 'YYYY-MM-DD' of the last restart
 * @param {number} [params.activeCount]       — active tickets on the board
 * @returns {boolean}
 */
export function shouldNightlyReload({ now, reloadHour = 4, lastReloadDay = null, activeCount = 0 }) {
  if (activeCount > 0) return false;             // never interrupt live service
  const d = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(d.getTime())) return false;
  if (d.getHours() !== reloadHour) return false;
  return localDateISO(d) !== lastReloadDay;      // once per calendar day
}
